import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    CircularProgress,
    Snackbar,
    Alert,
    Autocomplete,
    FormControlLabel,
    Checkbox,
    Typography,
    Box,
    Chip,
    Stack
} from '@mui/material';
import axios from '../../../api/axios';

/*
CreateAddressDialog
Props:
- open: boolean
- onClose: () => void
- onCreated: (addressObj) => void
- userId: id of user

Behavior: collects address fields, validates minimal required (name optional, area required). Allows choosing default (Delivery) toggle.
On submit POST to user/address/create/ with payload { user_id, ...fields }. Response assumed to include created address object (try to detect from common shapes). Pass created address to onCreated.
*/

const initialState = {
    name: '',
    receiver_full_name: '',
    phone_number: '',
    area_name: '',
    block: '',
    street: '',
    jaddah: '',
    house: '',
    floor: '',
    apartment: '',
    extra_direction: '',
    inner_note: '',
    assigned_driver: '',
    is_default: false
};

const NAME_SUGGESTIONS = ['Home', 'Office', 'Sunday', 'August', 'Partner'];

export default function CreateAddressDialog({ open, onClose, userId, onCreated, userDefaults }) {
    const [form, setForm] = useState(initialState);
    const [submitting, setSubmitting] = useState(false);
    const [areas, setAreas] = useState([]);
    const [loadingAreas, setLoadingAreas] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        if (!open) return;
        const defaults = {
            receiver_full_name: userDefaults?.receiver_full_name || '',
            phone_number: userDefaults?.phone_number || userDefaults?.phone || '',
            area_name: userDefaults?.area_name || userDefaults?.area || '',
            name: userDefaults?.name || '',
            block: userDefaults?.block || '',
            street: userDefaults?.street || '',
            jaddah: userDefaults?.jaddah || '',
            house: userDefaults?.house || '',
            floor: userDefaults?.floor || '',
            apartment: userDefaults?.apartment || '',
            extra_direction: userDefaults?.extra_direction || '',
            inner_note: userDefaults?.inner_note || '',
            assigned_driver: userDefaults?.assigned_driver || ''
        };
        setForm({ ...initialState, ...defaults });
        const fetchAreas = async () => {
            try {
                setLoadingAreas(true);
                const response = await axios.get('get_area_list/');
                if (Array.isArray(response.data)) {
                    setAreas(response.data);
                } else if (response.data?.areas && Array.isArray(response.data.areas)) {
                    setAreas(response.data.areas);
                } else if (response.data && typeof response.data === 'object') {
                    const arr = Object.keys(response.data).map(k => ({ name: k, ...response.data[k] }));
                    setAreas(arr);
                } else {
                    setAreas([]);
                }
            } catch (e) {
                console.error('Failed loading areas', e);
            } finally {
                setLoadingAreas(false);
            }
        };
        fetchAreas();
    }, [open, userDefaults]);

    const handleChange = (field) => (e) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleSubmit = async () => {
        if (!userId) return;
        if (!form.area_name) {
            setSnackbar({ open: true, message: 'Area is required', severity: 'error' });
            return;
        }
        setSubmitting(true);
        try {
            const payload = { user_id: userId, ...form };
            const res = await axios.post('user/address/create/', payload);
            let created = null;
            if (res.data) {
                if (res.data.address) created = res.data.address;
                else if (res.data.created_address) created = res.data.created_address;
                else if (res.data.id) created = res.data; // direct object
                else if (Array.isArray(res.data.addresses)) created = res.data.addresses[res.data.addresses.length - 1];
            }
            if (!created) {
                // fallback: merge payload with maybe returned id
                created = { id: res.data?.id || Date.now(), ...payload };
            }
            setSnackbar({ open: true, message: 'Address created', severity: 'success' });
            onCreated && onCreated(created);
        } catch (err) {
            console.error('Failed to create address', err);
            setSnackbar({ open: true, message: 'Create failed', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleSnackbarClose = () => setSnackbar(p => ({ ...p, open: false }));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Create New Address</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Box>
                            <TextField label="Address Name" value={form.name} onChange={handleChange('name')} size="small" fullWidth margin="dense" />
                            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mt: 0.5 }}>
                                {NAME_SUGGESTIONS.map(s => (
                                    <Chip
                                        key={s}
                                        label={s}
                                        size="small"
                                        variant={form.name?.toLowerCase() === s.toLowerCase() ? 'filled' : 'outlined'}
                                        color={form.name?.toLowerCase() === s.toLowerCase() ? 'primary' : 'default'}
                                        onClick={() => setForm(prev => ({ ...prev, name: s }))}
                                        sx={{ height: 22, fontSize: '0.65rem' }}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField label="Receiver" value={form.receiver_full_name} onChange={handleChange('receiver_full_name')} size="small" fullWidth margin="dense" disabled />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField label="Phone" value={form.phone_number} onChange={handleChange('phone_number')} size="small" fullWidth margin="dense" disabled />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Autocomplete
                            size="small"
                            loading={loadingAreas}
                            options={areas}
                            disablePortal
                            getOptionLabel={(option) => typeof option === 'string' ? option : (option.name || option.area || '')}
                            isOptionEqualToValue={(option, value) => {
                                if (typeof option === 'string' && typeof value === 'string') return option === value;
                                const optName = typeof option === 'string' ? option : (option.name || option.area || '');
                                const valName = typeof value === 'string' ? value : (value.name || value.area || '');
                                return optName === valName;
                            }}
                            value={form.area_name || null}
                            onChange={(e, newValue) => {
                                let area = '';
                                if (newValue) {
                                    if (typeof newValue === 'string') area = newValue; else area = newValue.name || newValue.area || '';
                                }
                                setForm(prev => ({ ...prev, area_name: area }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Area *"
                                    margin="dense"
                                    placeholder="Select area"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingAreas ? <CircularProgress size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    {['block', 'street', 'jaddah', 'house', 'floor', 'apartment', 'assigned_driver'].map(f => (
                        <Grid item xs={12} sm={6} md={4} key={f}>
                            <TextField label={f.charAt(0).toUpperCase() + f.slice(1)} value={form[f]} onChange={handleChange(f)} size="small" fullWidth margin="dense" />
                        </Grid>
                    ))}
                    <Grid item xs={12} sm={6} md={4}>
                        <FormControlLabel control={<Checkbox checked={form.is_default} onChange={(e) => setForm(p => ({ ...p, is_default: e.target.checked }))} />} label="Set as Delivery" />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="Extra Direction" value={form.extra_direction} onChange={handleChange('extra_direction')} size="small" fullWidth margin="dense" multiline minRows={2} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="Inner Note" value={form.inner_note} onChange={handleChange('inner_note')} size="small" fullWidth margin="dense" multiline minRows={2} />
                    </Grid>
                </Grid>
                <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color={form.area_name ? 'text.secondary' : 'error'}>Area is required *</Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} size="small">Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" size="small" disabled={submitting}>{submitting ? 'Creating…' : 'Create'}</Button>
            </DialogActions>
            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} onClose={handleSnackbarClose} variant="filled">{snackbar.message}</Alert>
            </Snackbar>
        </Dialog>
    );
}
